import { devRequested } from "../session/params";
import {
  BEAT_MOUTH,
  BEAT_ROAD,
  type EventId,
  isOnOrBefore,
  type SettlementId,
  type SpotlightBeat,
  type WorldDateView,
} from "../world/types";
import { stageableBeat } from "./staging";

/*
 * The director, v0: when the player meets what the simulation says is there.
 *
 * Phase 4 decision 1 splits this in two and the split is forced by the
 * architecture rather than chosen for convenience. **What is worth showing is
 * gameplay judgement and lives sim-side** — `sim_core::history::spotlight`
 * answers "for settlement S over the next N days, which witnessable beats
 * exist", and every beat carries the records behind it and the earliest date it
 * can honestly surface. **When you meet it depends on the player's body**, and
 * the player is not a sim entity (Phase 3 decision 10), so this module consumes
 * that feed plus the two inputs the simulation can never hold — where the player
 * is and what their journal already contains — and times *presentation only*.
 *
 * # The four rules, and they are absolute
 *
 * - **Source order, never re-ranked.** The list arrives sorted by the day each
 *   beat can surface (`spotlight`'s own `sort_unstable_by_key`), and this walks
 *   it front to back and takes the first thing it can use. A `sort` in this file
 *   would be a gameplay decision in JavaScript, which is the bug devlog #2 is
 *   named after.
 * - **Nothing before its date.** {@link SpotlightBeat.earliest} is checked by
 *   `staging.ts`'s {@link stageableBeat} and asserted a second time here, in
 *   {@link Director.due}, because a *held* beat is the one shape of this module
 *   that could drift into pre-echoing something.
 * - **Nothing is fabricated.** This module chooses between beats and moments. It
 *   never composes a beat, never edits one, and never puts a record in front of
 *   the player that the simulation did not offer.
 * - **Everything staged is filed.** {@link Director.log} keeps every beat this
 *   session staged with its `EventId`s, which is what the trace view shows and
 *   what the people view draws its cross-references from. A delivery nobody can
 *   check against the log is a delivery this phase does not claim.
 *
 * # What it decides, which is four things
 *
 * 1. **Which of its presentations fits a beat.** The client has exactly one
 *    staged sequence — the town turning out (decision 5) — and it fits some
 *    surfaces and not others; see {@link fitsTheStreet}.
 * 2. **Whether the player is there to see it.** The host loop steps the world
 *    while the click-to-play overlay is up, so an opening reaction would
 *    otherwise play to an empty room: a selected beat is *held* until the
 *    session is actually being played, and dropped if it goes stale
 *    ({@link DirectorConfig.holdSeconds}, {@link PENDING_DAYS}).
 * 3. **How close together deliveries may fall.** One reaction at a time, a
 *    quiet gap after it ({@link DirectorConfig.quietSeconds}), and a ceiling on
 *    how many a day may hold at all ({@link DirectorConfig.beatsPerDay}).
 * 4. **How often it looks.** The feed is read on the day boundary and offered
 *    to this module several times a day — see
 *    {@link DirectorConfig.watchesPerDay} and `witness/witness.ts`'s watch.
 *
 * # v1: the knobs became surface (Phase 5 decision 9)
 *
 * Phase 4 shipped every number below hard-coded, wrote down what each was for,
 * and put "director-as-editor — knobs, encounter budgets, quiet-time floors as
 * tunable surface" on its cut list for this phase. This is that: the four
 * numbers are a {@link DirectorConfig} with documented defaults, overridable
 * from the URL and **only behind `?dev=1`** ({@link directorKnobs}) — a player
 * never tunes the pacing of their own session, for the same reason the trace
 * view is not on their screen.
 *
 * The four absolute rules above are untouched by any of it. A knob may change
 * *how often* the director looks and *how much* it may spend; nothing here can
 * re-order the feed, surface a beat before its date, invent one, or stage one
 * without filing it.
 */

/**
 * The knobs. Every one is pacing; not one of them is judgement.
 *
 * Read once at startup and held for the session (`main.ts`), rather than
 * consulted through a mutable object: a director whose floor moved mid-session
 * would make the log it keeps unreadable — two beats a minute apart might have
 * been legal at one and not at the other, and nothing would say which.
 */
export interface DirectorConfig {
  /** Seconds of stepped time the street stays quiet after a reaction. */
  readonly quietSeconds: number;

  /** How many beats a day may be staged at all — the encounter budget. */
  readonly beatsPerDay: number;

  /** Seconds a chosen beat may wait for the player before it goes stale. */
  readonly holdSeconds: number;

  /** How many times a day the director is offered the feed. */
  readonly watchesPerDay: number;
}

/**
 * How long the street stays quiet after a reaction before another may begin, in
 * seconds of stepped time. PROVISIONAL.
 *
 * **Re-derived for twenty-minute days** (plan decision 9). Phase 4's forty-five
 * seconds was "a little over one in-game day" at the old calendar's forty-second
 * day, and the *shape* of that reasoning does not survive the retune: a little
 * over one day is now twenty minutes, which would mean one reaction per session
 * and no cadence at all. What the floor was actually protecting is unchanged and
 * still real — the abstract tier moves on an entity turn of 28 days
 * (`sim_core::history`'s `ENTITY_TURN_DAYS`) and lands nearly everything it has
 * to say on that one day, five caravans and the month's rumours and the month's
 * price moves at once — so the question is only how far apart two of those may
 * fall before they read as one event.
 *
 * An eighth of a day. At the pacing this phase ships that is two and a half
 * minutes of walking, which is long enough that the second turn-out is a second
 * piece of news rather than the same crowd, and short enough that a busy morning
 * can still be a busy morning. With {@link DEFAULT_BEATS_PER_DAY} it also means
 * the budget can never be spent in one clump.
 *
 * On the *old* forty-second-day builds this is about four days rather than one,
 * so a stale wasm paces slower than it used to. Accepted: the calendar it was
 * sized for is the one arriving, and the client is never told which world it is
 * in.
 */
const DEFAULT_QUIET_SECONDS = 150;

/**
 * How many beats a day may be staged. PROVISIONAL.
 *
 * **v0's budget was one-pending-at-a-time and implicit**, which was a bound on
 * *concurrency* rather than on frequency — with the day boundary as the only
 * offer, one a day fell out of it by accident. The watch cadence removes that
 * accident (a day now has several offers in it), so the ceiling has to be said
 * out loud or a busy month's worth of material would arrive as a busy hour.
 *
 * Three. A twenty-minute day with three turn-outs in it is a day with something
 * happening in it and time in between; a day with none is the ordinary case and
 * always was. The budget bounds *selection*: a beat already chosen is honoured,
 * because it was inside the budget when it was chosen and dropping it at the
 * last moment would be the director spending a delivery on nothing.
 */
const DEFAULT_BEATS_PER_DAY = 3;

/**
 * How long a chosen beat may wait for the player, in seconds of stepped time.
 * PROVISIONAL.
 *
 * **New in v1, and it exists because {@link PENDING_DAYS} stopped biting.** The
 * calendar bound below is a bound on a fiction and is still right; what it used
 * to *also* do, by accident of a forty-second day, was keep a held beat from
 * outliving the moment it belonged to. At twenty minutes a day it no longer
 * does: a beat chosen at first light could be held until dusk and then staged,
 * and a town turning out at nightfall over this morning's news is a reaction to
 * nothing — the exact failure the day bound was written against.
 *
 * A quarter of a day: chosen at first light, dropped by mid-morning. It is
 * comfortably longer than {@link DEFAULT_QUIET_SECONDS}, so a beat chosen while
 * the street is still quieting down survives to be delivered rather than
 * expiring in the gap.
 *
 * On the old forty-second-day builds this is seven days and the calendar bound
 * bites first, which means those sessions behave exactly as they did.
 */
const DEFAULT_HOLD_SECONDS = 300;

/**
 * How many times a day the director is offered the feed. PROVISIONAL.
 *
 * **The offer cadence, which is the other half of decision 9.** Phase 4 called
 * `offer` on the day boundary and nowhere else, because the day boundary is the
 * only moment a beat can *become* today's — and at forty seconds a day that was
 * a scheduling decision every forty seconds. At twenty minutes it is one
 * decision per twenty minutes, which is not a director, it is a coin flip.
 *
 * Six watches: one at first light and one every two hundred seconds after it.
 * The client keeps them (`witness/witness.ts` owns the clock, because pacing is
 * client-side and the calendar is not), and what changes between two watches of
 * the same day is real — the player has walked somewhere, the journal has
 * learned something, a column has moved down its road. Six is enough that a
 * beat which failed one test can pass a later one within the morning, and few
 * enough that the encounter budget rather than the cadence is what decides how
 * busy a day is.
 */
const DEFAULT_WATCHES_PER_DAY = 6;

/** The knobs as they ship. Everything above, in one object. */
export const DIRECTOR_DEFAULTS: DirectorConfig = {
  quietSeconds: DEFAULT_QUIET_SECONDS,
  beatsPerDay: DEFAULT_BEATS_PER_DAY,
  holdSeconds: DEFAULT_HOLD_SECONDS,
  watchesPerDay: DEFAULT_WATCHES_PER_DAY,
};

/**
 * How long a selected beat may wait for the player, in in-game days.
 * PROVISIONAL.
 *
 * One day, and the number is a *bound on a fiction* rather than a pacing knob —
 * which is why it stayed a constant while the other four became surface.
 * Delay is explicitly the director's to spend (decision 1: "it may delay and
 * space"), but a town that turns out a week after the news came would be a
 * reaction to nothing, so a held beat is dropped once the calendar has moved
 * past it. Re-checked at twenty-minute days and kept: a day is still exactly
 * the span over which "this morning's news" stops being this morning's, whatever
 * a day costs in wall time. What *did* change is that it is no longer the bound
 * that usually bites — see {@link DEFAULT_HOLD_SECONDS}.
 */
export const PENDING_DAYS = 1;

/** `?director=quiet:150,beats:3,hold:300,watches:6` — the knobs, behind `?dev=1`. */
const DIRECTOR_PARAM = "director";

/**
 * What a knob may be set to.
 *
 * Bounds rather than free numbers, because a URL is somebody typing: `beats:300`
 * is a fat finger and a session that honoured it would look like a bug in the
 * director. The ranges are deliberately wide — this is a fence around nonsense,
 * not a second opinion about pacing — and anything outside one is refused out
 * loud rather than clamped, because a session that quietly ran different pacing
 * than the link asked for is an unreproducible session (`session/params.ts`
 * makes the same call about a `?home=` that names nowhere).
 */
const KNOB_RANGES = {
  quiet: { min: 0, max: 1_200 },
  beats: { min: 0, max: 24 },
  hold: { min: 1, max: 3_600 },
  watches: { min: 1, max: 48 },
} as const;

/** A config, and everything about the request this build would not do. */
export interface DirectorKnobs {
  readonly config: DirectorConfig;

  /** One line per thing refused, for the HUD to say out loud. Empty is the ordinary case. */
  readonly refused: readonly string[];
}

/**
 * Reads the knobs out of a query string.
 *
 * `?director=quiet:60,beats:1` — one parameter carrying a list, rather than
 * four parameters, because they are one decision and a link that names two of
 * them should not have to be read three times to see that. Unknown keys and
 * out-of-range values are refused and reported; a parameter this build does not
 * understand at all is still a parameter somebody typed on purpose.
 *
 * **Behind `?dev=1`, and the gate is checked here rather than at the call
 * site**, so there is exactly one place that can be got wrong. Pacing is not a
 * player-facing setting: the playtest protocol depends on every playtester's
 * session being paced the same way, and a link with a knob in it that quietly
 * worked would break that without anybody noticing (`session/params.ts` and
 * devlog #10's postscript, same rule).
 *
 * Takes the search string rather than reading `window`, like everything else
 * in this client that parses one — the self-checks ask the awkward questions
 * without a browser.
 */
export function directorKnobs(search: string): DirectorKnobs {
  const raw = new URLSearchParams(search).get(DIRECTOR_PARAM);

  if (raw === null || raw.trim() === "") {
    return { config: DIRECTOR_DEFAULTS, refused: [] };
  }

  if (!devRequested(search)) {
    return {
      config: DIRECTOR_DEFAULTS,
      refused: [`?${DIRECTOR_PARAM}= is a development knob and needs ?dev=1 — using the defaults`],
    };
  }

  const refused: string[] = [];
  const wanted = { ...DIRECTOR_DEFAULTS };

  for (const clause of raw.split(",")) {
    const trimmed = clause.trim();

    if (trimmed === "") {
      continue;
    }

    const [name = "", value = ""] = trimmed.split(":");
    const key = name.trim().toLowerCase();
    const range = key === "" ? undefined : KNOB_RANGES[key as keyof typeof KNOB_RANGES];

    if (range === undefined) {
      refused.push(`?${DIRECTOR_PARAM}: no knob called “${key}”`);

      continue;
    }

    const number = Number(value.trim());

    if (!Number.isFinite(number) || number < range.min || number > range.max) {
      refused.push(`?${DIRECTOR_PARAM}: ${key} must be ${range.min}..${range.max}, not “${value}”`);

      continue;
    }

    switch (key) {
      case "quiet":
        wanted.quietSeconds = number;
        break;

      case "beats":
        wanted.beatsPerDay = Math.floor(number);
        break;

      case "hold":
        wanted.holdSeconds = number;
        break;

      case "watches":
        wanted.watchesPerDay = Math.floor(number);
        break;
    }
  }

  return { config: wanted, refused };
}

/** One beat this session staged, with everything a debug view needs to check it. */
export interface StagedBeat {
  readonly beat: SpotlightBeat;

  /** The day it was staged. Never earlier than {@link SpotlightBeat.earliest}. */
  readonly when: WorldDateView;

  /** What the player would call the town it was staged in. */
  readonly where: string;
}

/** What the director needs from the client to make its two client-side decisions. */
export interface DirectorInputs {
  /** The feed, in the order the simulation returned it. Never sorted here. */
  readonly beats: readonly SpotlightBeat[];

  /** The settlement the player is standing in. */
  readonly here: SettlementId;

  readonly today: WorldDateView;

  /**
   * Whether the player has already learned a record.
   *
   * The journal, asked (`journal/journal.ts`). This is one of the two inputs
   * decision 1 gives the client and the sim can never have: a town turning out
   * to tell the player something they wrote down an hour ago is a delivery
   * spent on nothing.
   */
  readonly known: (event: EventId) => boolean;

  /**
   * Whether a refugee column on the roads is carrying this beat's records.
   *
   * The join that tells a column from a trade pulse, which the surface alone
   * cannot: both arrive as {@link BEAT_ROAD}. It is also what lets the staged
   * reaction point smoke at a town and mean it — see {@link fitsTheStreet}.
   */
  readonly carried: (beat: SpotlightBeat) => boolean;
}

export class Director {
  /** The knobs this session runs under. Fixed for its lifetime; see {@link DirectorConfig}. */
  private readonly config: DirectorConfig;

  /** Every beat staged this session, oldest first. */
  private readonly staged: StagedBeat[] = [];

  /** Their ids, which is what the selection is held to. */
  private readonly stagedIds = new Set<number>();

  /** A beat chosen and waiting for the player, or `null`. */
  private pending: SpotlightBeat | null = null;

  /** Seconds of stepped time since the last reaction ended. */
  private quiet: number;

  /** Seconds of stepped time the beat above has been waiting. See {@link DEFAULT_HOLD_SECONDS}. */
  private waited = 0;

  /**
   * The day the encounter budget is being counted against, and how much of it
   * is spent.
   *
   * The day rather than a rolling window, because the budget is stated in days
   * and a day is the one span both halves of the client agree on: the
   * simulation names it and the client watches for it to change
   * (`world/types.ts`). Rolled over in {@link Director.offer}, which is called
   * at least once a day by construction — the first watch of a day stands on
   * the boundary itself.
   */
  private budgetDay: WorldDateView | null = null;
  private spent = 0;

  /** How many watches have been stood this session. See {@link Director.offers}. */
  private watches = 0;

  constructor(config: DirectorConfig = DIRECTOR_DEFAULTS) {
    this.config = config;
    // Opening quiet, so the first beat of a session is not held back by a gap
    // after a reaction that never happened.
    this.quiet = config.quietSeconds;
  }

  /** Everything staged, for the trace view and the people view. */
  get log(): readonly StagedBeat[] {
    return this.staged;
  }

  /** The beat waiting to be staged, if any. The self-checks read it. */
  get held(): SpotlightBeat | null {
    return this.pending;
  }

  /** How much of today's encounter budget has been spent. The self-checks read it. */
  get spentToday(): number {
    return this.spent;
  }

  /**
   * How many times the feed has been offered this session — the watches stood.
   *
   * Kept for the self-checks, which is the only honest way to assert a
   * *cadence*: whether the director is asked six times a day or once is
   * invisible in what it stages, because most watches of most days have nothing
   * to choose. Costs an increment.
   */
  get offers(): number {
    return this.watches;
  }

  /**
   * Advances the quiet clock and the held beat's clock by one fixed step.
   *
   * Stepped rather than timed off a wall clock for the reason everything else in
   * the witnessing layer is: a slow frame or a backgrounded tab must not spend
   * the gap the player is supposed to experience (`host/timestep.ts`).
   *
   * The waiting clock runs *through* a reaction as well as between two, and
   * that is the point of it: a beat chosen while the street is already turned
   * out is a beat whose morning is being spent on something else, and the
   * whole question it answers is whether it will still be worth showing when
   * the street is free.
   */
  step(stepSeconds: number, reacting: boolean): void {
    if (this.pending !== null) {
      this.waited += stepSeconds;
    }

    if (reacting) {
      this.quiet = 0;

      return;
    }

    this.quiet += stepSeconds;
  }

  /**
   * Looks at the feed and chooses at most one beat to stage.
   *
   * Called on a **watch** — several times a day rather than once, since Phase 5
   * decision 9 (`witness/witness.ts` keeps the clock; see
   * {@link DEFAULT_WATCHES_PER_DAY} for why the day boundary alone stopped
   * being enough). The selection is {@link stageableBeat}'s — about somewhere
   * else, landing today, never staged before — narrowed by the two client-side
   * questions this module owns: does the client have a presentation that fits
   * it, and does the player already know everything it carries.
   *
   * **A watch changes nothing about what may be chosen.** It is the same feed
   * in the same order and the same date test; what moves between two watches of
   * one day is the *client's* half — where the player is standing, what the
   * journal now holds, which columns are on the roads. A beat refused at first
   * light and taken at noon was refused for a client-side reason that stopped
   * being true, which is exactly the decision this module exists to make.
   *
   * A beat already held is kept: the town has one morning, and a second beat
   * chosen before the first was shown would be a queue of reactions nobody
   * asked for.
   */
  offer(inputs: DirectorInputs): void {
    this.watches += 1;
    this.rollBudget(inputs.today);

    if (this.pending !== null) {
      return;
    }

    if (this.spent >= this.config.beatsPerDay) {
      // Today's budget is gone. Nothing is *lost* by this: the material is
      // still in the world and the player can still walk into it — what the
      // budget declines is one more staged reaction on top of the ones this
      // day already had (see {@link DEFAULT_BEATS_PER_DAY}).
      return;
    }

    const chosen = stageableBeat(
      inputs.beats,
      inputs.here,
      inputs.today,
      this.stagedIds,
      (beat) => fitsTheStreet(beat, inputs.carried) && !this.allKnown(beat, inputs.known),
    );

    if (chosen === null) {
      return;
    }

    this.pending = chosen;
    this.waited = 0;
  }

  /**
   * Returns the beat to stage right now, or `null`.
   *
   * Called every step, because the two things it waits on move on different
   * clocks: the calendar picks the beat and the *player* decides when the room
   * is occupied. `playing` is the client's own fact — the first input of the
   * session, the same one that dismisses the key card (`input/controls.ts`).
   *
   * The date is checked again here rather than trusted from {@link offer}: this
   * is the one path in the module where a beat crosses a day boundary, and the
   * rule it could break — nothing surfaces before its record lands — is the
   * rule the whole arrangement exists to make impossible.
   */
  due(today: WorldDateView, playing: boolean, reacting: boolean): SpotlightBeat | null {
    const beat = this.pending;

    if (beat === null) {
      return null;
    }

    if (!isOnOrBefore(beat.earliest, today)) {
      // Cannot happen through `offer`, which only ever selects a beat landing
      // today. Refused rather than corrected, because a director that quietly
      // fixed this would hide the day the projection changed shape.
      this.pending = null;

      return null;
    }

    if (daysLate(today, beat.earliest) > PENDING_DAYS) {
      // The player never turned up, and the morning has gone. The material is
      // still in the world — the rumour is still in mouths, the column still on
      // the road — and the player can still walk into it; what is dropped is
      // only this client's staging of it.
      this.pending = null;

      return null;
    }

    if (this.waited > this.config.holdSeconds) {
      // The same drop on the clock the *player* lives on rather than the one
      // the calendar does. At twenty-minute days this is the bound that
      // actually bites; see {@link DEFAULT_HOLD_SECONDS}.
      this.pending = null;

      return null;
    }

    if (!playing || reacting || this.quiet < this.config.quietSeconds) {
      return null;
    }

    this.pending = null;

    return beat;
  }

  /** Files a beat as staged. The caller has just begun the reaction. */
  begun(beat: SpotlightBeat, today: WorldDateView, where: string): void {
    this.rollBudget(today);
    this.stagedIds.add(beat.id);
    this.staged.push({ beat, when: today, where });
    this.quiet = 0;
    this.waited = 0;
    this.spent += 1;
  }

  /** Whether every record a beat carries is already in the journal. */
  private allKnown(beat: SpotlightBeat, known: (event: EventId) => boolean): boolean {
    return beat.refs.length > 0 && beat.refs.every((event) => known(event));
  }

  /**
   * Starts today's encounter budget over if the date has turned.
   *
   * Compared rather than counted down, because the client owns no calendar
   * arithmetic: "is this the day the budget belongs to" is two integers, and
   * anything harder is a question for the simulation (`world/types.ts`).
   */
  private rollBudget(today: WorldDateView): void {
    const day = this.budgetDay;

    if (day !== null && day.year === today.year && day.dayOfYear === today.dayOfYear) {
      return;
    }

    this.budgetDay = today;
    this.spent = 0;
  }
}

/**
 * Whether the client's one staged sequence is an honest way to show this beat.
 *
 * **This is a question about presentation, not about importance**, and the
 * difference is what keeps it on the right side of the phase's cross-cutting
 * rule. Nothing here re-ranks the feed or decides what matters; it asks whether
 * *the town turning out at the board* is a true picture of a given beat, and the
 * thing it switches on is {@link SpotlightBeat.channel} — the surface, which
 * `world/types.ts` says exists precisely so the client can switch on the place
 * it has to draw.
 *
 * Two surfaces pass, and each for a reason that is about what the beat's
 * `subject` actually means in the live projection:
 *
 * - **{@link BEAT_MOUTH}** — a rumour this town heard today, whose subject is
 *   the settlement that told it (`spotlight`'s `words`: `rumor.via`). The street
 *   repeating what came in this morning is exactly that, and `speakOf` can
 *   always answer for it: the record is in this town's store by construction.
 * - **{@link BEAT_ROAD}, when a refugee column is carrying it** — people
 *   arriving with the news, whose subject is the settlement they fled
 *   (`spotlight`'s `refugees`: `payload.defender`). That is the one beat in the
 *   feed whose subject is a town that was actually *struck*, which is what makes
 *   it the only one this client will point smoke at.
 *
 * The other two are refused, and the refusals are the load-bearing half:
 *
 * - a **caravan** is also {@link BEAT_ROAD}, and its subject is the other end of
 *   the road (`pulse.other_end`) — a town nothing has happened to. A crowd
 *   converging on the board and smoke on that bearing would say a trading
 *   partner was burning. Nothing is drawn on the road for a pulse now, so this
 *   refusal is the whole of what the client does with one.
 * - a **{@link BEAT_BOARD}** price beat carries a subject resolved from the
 *   record its shock blames, and `related_settlements()` yields the *attacker*
 *   first for a battle — so the bearing points at the army's home rather than at
 *   the sack. The board is a channel the player reads, and it is one press away
 *   the whole time; it does not need a crowd around it to be found.
 * - a **ground** beat stands where it is and always did. There is nothing to
 *   react to.
 */
export function fitsTheStreet(
  beat: SpotlightBeat,
  carried: (beat: SpotlightBeat) => boolean,
): boolean {
  if (beat.channel === BEAT_MOUTH) {
    return true;
  }

  return beat.channel === BEAT_ROAD && carried(beat);
}

/**
 * How many days late a date is, as far as the client is allowed to count.
 *
 * The client does not know how long a year is (`world/types.ts`), so a date in
 * an earlier year is "longer ago than anything this bound is measured in" — the
 * same answer, and the same reasoning, as `witness/witness.ts`'s `daysSince`.
 */
function daysLate(today: WorldDateView, landed: WorldDateView): number {
  if (landed.year < today.year) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.max(0, today.dayOfYear - landed.dayOfYear);
}
